// The keypad walk-through: press the keys a caller presses, see each menu's
// hours, prompts and after-hours behaviour, and the department at the end.

import { useEffect } from 'react';
import { RotateCcw, ArrowLeft } from 'lucide-react';
import { useGuide } from './context';
import { nodeName, shortestPath } from './model';
import { Card, Badge, Breadcrumb, Keypad, HoursTable, HolidayList, OpenBadge, SectionHead, type KeypadOption } from './components';
import { AudioPlayer } from './AudioPlayer';
import { QueueDetail } from './QueueDetail';
import { effectiveHours, compactHours, type DialPath } from './lib/callFlowStatus';
import { dayShort } from './i18n';
import type { AutoAttendantNode, CallFlowNode } from '../../api/client';

export function SimulatorSection({ path, setPath }: { path: string[]; setPath: (p: string[]) => void }) {
  const { t, model, lang, navigate } = useGuide();
  const currentId = path[path.length - 1] || model.rootId;
  const node = model.byId.get(currentId);

  // Keyboard: digits/*/# choose an option, Backspace goes back, Escape restarts.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable)) return;
      if (e.key === 'Backspace') { if (path.length > 1) { e.preventDefault(); setPath(path.slice(0, -1)); } return; }
      if (e.key === 'Escape') { setPath([model.rootId]); return; }
      if (node?.type === 'autoAttendant' && /^[0-9*#]$/.test(e.key)) {
        const opt = node.menu.find(m => m.key === e.key) || (e.key === '0' && node.operatorTargetId ? { targetId: node.operatorTargetId } : undefined);
        if (opt) { e.preventDefault(); setPath([...path, opt.targetId]); }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [path, node, model.rootId, setPath]);

  if (!node) {
    return <SectionHead title={t('callflowTitle')} intro={t('unknown')} />;
  }

  // The route matching the current walk (for effective hours); fall back to the shortest.
  const walkedPath: DialPath | undefined = (model.pathsByTarget.get(currentId) || [])
    .find(p => [model.rootId, ...p.steps.map(s => s.toId)].join('|') === path.join('|')) || shortestPath(model, currentId);

  const crumbs = path.map((id, i) => ({
    label: nodeName(model.byId.get(id), lang),
    onClick: i < path.length - 1 ? () => setPath(path.slice(0, i + 1)) : undefined,
  }));

  return (
    <div className="space-y-4">
      <SectionHead title={t('callflowTitle')} intro={t('callflowIntro')} actions={
        <>
          <button type="button" onClick={() => setPath(path.slice(0, -1))} disabled={path.length <= 1}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40">
            <ArrowLeft className="w-3.5 h-3.5" />{t('back')}
          </button>
          <button type="button" onClick={() => setPath([model.rootId])}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50">
            <RotateCcw className="w-3.5 h-3.5" />{t('restart')}
          </button>
        </>
      } />
      <Breadcrumb items={crumbs} />

      {node.type === 'autoAttendant' ? (
        <MenuStep aa={node} walkedPath={walkedPath} onChoose={id => setPath([...path, id])} />
      ) : node.type === 'queue' ? (
        <>
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-1"><Badge kind="dark">{t('callQueue')}</Badge></div>
            <h2 className="text-xl font-semibold text-gray-900">{nodeName(node, lang)}</h2>
            <div className="text-xs text-gray-500">{node.label}</div>
          </Card>
          <QueueDetail queue={node} />
        </>
      ) : (
        <TerminalStep node={node} />
      )}
      {node.type === 'queue' && (
        <button type="button" onClick={() => navigate({ section: 'departments', id: node.id })} className="text-xs text-purple-700 hover:underline print:hidden">{t('viewDepartment')} →</button>
      )}
    </div>
  );
}

function MenuStep({ aa, walkedPath, onChoose }: { aa: AutoAttendantNode; walkedPath?: DialPath; onChoose: (id: string) => void }) {
  const { t, flow, model, lang } = useGuide();
  const eff = effectiveHours(flow, walkedPath);
  const ownWeekly = aa.id === model.rootId ? (aa.hours || flow.schedule.weekly) : aa.hours;
  const ownHolidays = aa.id === model.rootId ? (aa.holidays?.length ? aa.holidays : flow.schedule.holidayRanges) : aa.holidays;
  const options: KeypadOption[] = aa.menu.map(m => {
    const target = model.byId.get(m.targetId);
    return {
      key: m.key, targetId: m.targetId,
      title: target ? nodeName(target, lang) : m.label,
      sub: target?.type === 'autoAttendant' ? t('autoAttendant') : target?.type === 'queue' ? t('callQueue') : t('terminal'),
    };
  });
  if (aa.operatorTargetId && !aa.menu.some(m => m.key === '0')) {
    const target = model.byId.get(aa.operatorTargetId);
    options.push({ key: '0', targetId: aa.operatorTargetId, title: target ? nodeName(target, lang) : t('operator'), sub: t('operator') });
  }
  const afterTarget = aa.afterHoursTargetId ? model.byId.get(aa.afterHoursTargetId) : (aa.id === model.rootId ? model.byId.get(flow.closedTargetId) : undefined);

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-1">
          <Badge kind="green">{t('autoAttendant')}</Badge>
          <OpenBadge weekly={eff.weekly} holidays={eff.holidays} iana={eff.iana} />
          {aa.language && <Badge kind="gray">{aa.language}</Badge>}
        </div>
        <h2 className="text-xl font-semibold text-gray-900">{nodeName(aa, lang)}</h2>
        <div className="text-xs text-gray-500">{aa.label}</div>
      </Card>

      <section>
        <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('chooseOption')}</h3>
        {options.length ? <Keypad options={options} onSelect={onChoose} /> : <div className="text-xs text-gray-400">{t('noOptions')}</div>}
      </section>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('businessHours')}</h3>
          {ownWeekly
            ? <HoursTable weekly={ownWeekly} compact />
            : <div className="text-xs text-gray-500 bg-green-50 border border-green-100 rounded px-2.5 py-2">{t('inherited')}</div>}
          <div className="mt-3 text-xs bg-gray-50 border border-gray-100 rounded px-2.5 py-2">
            <div className="font-medium text-gray-800">{t('effectiveHours')}</div>
            <div className="text-gray-600">{eff.weekly ? compactHours(eff.weekly, dayShort(lang), t('closed')) : t('hoursUnknown')}</div>
          </div>
          {(ownHolidays?.length || 0) > 0 && (
            <div className="mt-3">
              <div className="text-xs font-medium text-gray-800 mb-1">{t('holidays')}</div>
              <HolidayList holidays={ownHolidays} />
            </div>
          )}
        </Card>
        <Card className="p-4 space-y-2">
          <h3 className="text-sm font-semibold text-gray-900">{t('prompt')}</h3>
          <AudioPlayer prompt={aa.greetingPrompt} label={t('greeting')} voiceLang={aa.language} />
          <AudioPlayer prompt={aa.menuPrompt} label={t('menuPrompt')} voiceLang={aa.language} />
          {!aa.greetingPrompt && !aa.menuPrompt && aa.greeting && <p className="text-sm text-gray-700 italic">“{aa.greeting}”</p>}
          {!aa.greetingPrompt && !aa.menuPrompt && !aa.greeting && <div className="text-xs text-gray-400">—</div>}
          <div className="text-xs bg-amber-50 border border-amber-100 rounded px-2.5 py-2 mt-2">
            <div className="font-medium text-amber-900">{t('afterHours')}</div>
            <div className="text-amber-800">
              {afterTarget
                ? (afterTarget.type === 'disconnect' ? t('afterHoursDisconnect') : `${t('afterHoursRoutes')} ${nodeName(afterTarget, lang)}`)
                : t('parentHours')}
            </div>
            {afterTarget && afterTarget.type === 'autoAttendant' && (afterTarget.greetingPrompt || afterTarget.menuPrompt) && (
              <div className="mt-2 space-y-1.5">
                <AudioPlayer prompt={afterTarget.greetingPrompt} label={t('greeting')} voiceLang={afterTarget.language} />
                <AudioPlayer prompt={afterTarget.menuPrompt} label={t('menuPrompt')} voiceLang={afterTarget.language} />
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

function TerminalStep({ node }: { node: CallFlowNode }) {
  const { t, lang } = useGuide();
  const kind = node.type === 'voicemail' ? t('voicemail') : node.type === 'disconnect' ? t('disconnect') : node.type === 'external' ? t('forwardTo') : t('person');
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 mb-1"><Badge kind="amber">{t('terminal')}</Badge></div>
      <h2 className="text-xl font-semibold text-gray-900">{nodeName(node, lang)}</h2>
      <div className="text-sm text-gray-600 mt-1">{kind}{'detail' in node && node.detail ? ` · ${node.detail}` : ''}</div>
    </Card>
  );
}
